import { canv } from "@/canv/global.js";
import {
    getBoolProperty,
    getNumberProperty,
    getStringProperty,
} from "@/canv/utils.js";
import { getViewport, processDataFromClipboard, saveTextToClipboard } from "@/canvasController/helpers";
import { elementBase } from "@/elementBase/elementBase.js";
import { textItem, textLine } from "@/elements/text/textLine.js";
import { textSelection } from "@/elements/text/textSelection.js";
import { showSnackbar } from "@/snackbar/snackbar";
import { getFontSize, isMobile } from "@/utils.js";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import {
    MAX_TEXT_LENGTH,
    SNACKBAR_LIMIT_TEXT_1,
    SNACKBAR_LIMIT_TEXT_2,
    SNACKBAR_TIME,
} from "./constants.js";
import {
    characterCounter,
    convertTextToLines,
    countCharactersOfText,
    cutHtmlTextBySymbolsCount,
    getActualSelection,
    splitStringByMaxSymbols,
} from "./utils.js";
import { selectionManager } from "@/tools/cursorTool/SelectionManager.js";
import { eventEmitter } from "@/eventEmitter/index.js";

const TEXT_RATIO_FOR_PLACEHOLDER = 0.0055;

export class textWidget extends elementBase {
    constructor(startX, startY) {
        super();
        this.startX = startX;
        this.startY = startY;

        this.lines.push(new textLine(0, this, this.bullettedList, this.align));

        this.selection = new textSelection(this);

        this.nothingWhenInteraction = true;
        this.nothingWhenInteractionLowOutline = true;

        this.updateStylesValuesAndDims();
    }

    // Dims ------------------------------------------------------------------------

    setKeepMinWidth(value) {
        this.keepMinWidth = value;
        this.updateStylesValuesAndDims();
    }

    setGreyText(text) {
        this.greyText = text;
        this.updateStylesValuesAndDims();
    }

    updateStylesValuesAndDims() {
        const ghost = new textItem(
            "A",
            this.fontSize,
            this.fontStyle,
            this.fontColor,
            this.fontBold,
        );
        this.emptyLineHeight = ghost.getHeight();
        if (this.keepMinWidth) {
            this.onlyEmptyLineWidth = 10 * ghost.getWidth(); // ~10 символов (еще паддинги)
        } else {
            this.onlyEmptyLineWidth = 0;
        }

        if (this.greyText != "") {
            // если создавать textItem, бегать и складывать item.getWidth(), то ширина не совпадает с нарисованной.
            canv.ctx.save();
            canv.ctx.font =
                this.fontBold +
                " " +
                this.fontSize.toString() +
                "px " +
                this.fontStyle.toString();
            this.greyTextWidth = canv.ctx.measureText(this.greyText).width;
            canv.ctx.restore();
        }

        this.updateDims();
    }

    shouldUseGreyText() {
        if (this.greyText != "" && this.isOnlyEmptyLine()) return true;
        return false;
    }

    isOnlyEmptyLine() {
        if (this.lines.length == 1 && this.lines[0].items.length == 0) return true;
        return false;
    }

    updateDims() {
        this.symbolsCount = 0;

        for (var i = 0; i < this.lines.length; i++) {
            this.lines[i].updateWidthHeight();
        }

        let textWidth = 0;
        for (var i = 0; i < this.lines.length; i++) {
            if (this.lines[i].textWidth > textWidth) {
                textWidth = this.lines[i].textWidth;
            }
        }

        this.width = 0;
        if (this.shapeMode) {
            this.width = this.hardWidth;
            this.textWidthForMiro = textWidth;
        } else {
            this.width = textWidth;
        }

        this.height = 0;
        for (var i = 0; i < this.lines.length; i++) {
            this.height += this.lines[i].textHeight;
        }

        var lastLineYBottom = undefined;
        for (var i = 0; i < this.lines.length; i++) {
            this.symbolsCount += this.lines[i].items.length;
            this.lines[i].updatePosition(lastLineYBottom, this.startX, i);
            lastLineYBottom = this.lines[i].startY;
        }

        this.updateBase(
            this.startX,
            this.startY - this.lines[0].textHeight,
            this.width,
            this.height,
        );
    }

    // -------------------------------------------------------------------------------

    // Interaction --------------------------------------------------------------------------------------

    async TryAction(type) {
        if (this.isLinkEdit) {
            if (type == "mouseup" && canv.actionManager.isClick()) {
                this.isLinkEdit = false;
                canv.appInterface.linkWidgetMenuWindow.hide();
                canv.widgetMenu.reopen();
                canv.redraw();
            } else if (
                type == "touchpadGestureDragging" ||
                type == "touchpadGestureZoom"
            ) {
                canv.appInterface.linkWidgetMenuWindow.locate();
            }
            return;
        }

        if (this.isEditTextMobile) {
            if (type == "mouseup" && canv.actionManager.isClick()) {
                this.isEditTextMobile = false;
                this.unfocusText();
                canv.widgetMenu.reopen();
                canv.redraw();
            } else if (
                type == "touchpadGestureDragging" ||
                type == "touchpadGestureZoom"
            ) {
                canv.appInterface.editTextWindow.locate();
            }
            return;
        }

        if (type == "keyboardDown") {
            if (canv.keyboard.isCopyCommand()) {
                this.processCopy();
                return;
            } else if (canv.keyboard.isPasteCommand()) {
                await this.processPaste();
            } else if (canv.keyboard.isCutCommand()) {
                this.processCopy();
                this.selection.removeSelection();
            } else if (canv.keyboard.isSelectAll()) {
                this.selection.selectAll();
            } else if (canv.keyboard.isArrow()) {
                this.processArrow();
            } else if (canv.keyboard.isArrowShift()) {
                this.processArrowShift();
            } else if (canv.keyboard.isEscCommand()) {
                // stopInteraction вызывается внутри cursorTool при изменении mode
                if (this.parent != null) {
                    canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                }
            } else if (canv.keyboard.isEnter()) {
                if (!this.onlyLineMode) {
                    this.selection.removeSelection();
                    this.processEnter();
                }
            } else if (canv.keyboard.isBackspace()) {
                this.processBackspace();
            } else if (canv.keyboard.isDelete()) {
                this.processDelete();
            } else if (canv.keyboard.isShiftEnd()) {
                this.selection.selectToLineEnd();
            } else if (canv.keyboard.isShiftHome()) {
                this.selection.selectToLineStart();
            } else if (canv.keyboard.isSymbol) {
                this.selection.removeSelection();
                this.processSymbol();
            }

            this.updateDims();
            this.Fit();

            if (this.isOpenedMyLink()) {
                canv.appInterface.textWidgetLinkWindow.hide();
            }
        } else if (type == "mousedown") {
            this.selection.startSelection();
        } else if (type == "mouseup") {
            this.selection.stopSelection();
        } else if (type == "mousemove") {
            this.selection.updateSelection();
        } else if (type == "dblclick") {
            this.selection.wordSelect();
        } else if (type === "tripleclick") {
            this.selection.lineSelect();
        }

        this.updateDims();
        canv.redraw();

        this.TryLeaveInteraction(type);
    }

    TryLeaveInteraction(type) {
        if (this.parent == null) return; // так мы отличаем text как виджет и text как часть другого виджета

        if (type == "mouseup" && canv.actionManager.isClick() && !this.isOver()) {
            // тут важно сбросить mode перед stopInteraction. Иначе stopInteraction->clearSelected->stopInteraction->clearSelected->...
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    isInteraction(context, force = false) {
        if ((context === "focus" && this.isOver()) || force) {
            this.fixState("focus");
            this.focusText();
            return {
                type: "interaction",
            };
        }

        if (context === "insertText" || context === "pastText") {
            this.fixState("focus");
            if (canv.keyboard.isEnter()) {
                this.focusTextByEnter();
            } else if (!canv.keyboard.isEnter()) {
                this.focusText();
                this.selection.selectAll();
                this.processDelete();
                this.TryAction("keyboardDown");
            }
            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    stopInteraction() {
        // stopInteraction вызывается только у text как виджета (не часть другого виджета).
        this.unfocusText();

        if (this.symbolsCount != 0) {
            canv.eventManager.openRecord();
            this.saveState("focus");
            canv.eventManager.closeRecord();
        } else {
            canv.eventManager.openRecord();
            canv.canvasController.deleteProcessor(this.parent);
            canv.eventManager.closeRecord();
            // Делаем так, чтобы внутри softResetMode не сохранить interaction для уже удаленного entity + не создать бесконечную рекурсию. Аналогично тому, как это вызывается в buildEvent
            canv.toolBox.TryGetToolByName("cursorTool").mode = "";
            selectionManager.clearSelected();
        }
    }

    // -------------------------------------------------------------------------------

    // draw ---------------------------------------------------------------------------

    draw() {
        const viewport = getViewport();
        const drawPlaceholder = this.lines.some(
            (line) =>
                line.textHeight / viewport["viewportHeight"] >
                TEXT_RATIO_FOR_PLACEHOLDER,
        )
            ? false
            : true;

        canv.ctx.save();
        this.clipArea();
        this.lines.forEach((line) => {
            if (drawPlaceholder && !canv.isHighPdfExporting) {
                line.drawPlaceholder();
            } else {
                line.draw();
            }
        });
        canv.ctx.restore();

        if (this.isFocused) {
            this.selection.draw();
        }

        if (!drawPlaceholder) {
            this.linkInterfaceShow();
        }
    }

    clipArea() {
        const startYWithLine = this.startY - this.lines[0].textHeight;
        canv.ctx.beginPath();
        canv.ctx.moveTo(this.startX, startYWithLine);
        canv.ctx.lineTo(this.startX + this.width, startYWithLine);
        canv.ctx.lineTo(this.startX + this.width, startYWithLine + this.height);
        canv.ctx.lineTo(this.startX, startYWithLine + this.height);
        canv.ctx.lineTo(this.startX, startYWithLine);

        canv.ctx.clip();
    }

    // --------------------------------------------------------------------------------

    // FITS -------------------------------------------------------------------------------

    Fit() {
        if (this.stickerMode) {
            this.fitSticker();
        } else if (this.shapeMode) {
            this.fitShape();
        }
    }

    fitSticker() {
        this.updateDims();

        var selectionSequentialPosition = this.selection.getSelectionSequential();

        // Идея:
        // увеличиваем fontSize пока высота и ширина меньше hard
        // Уменьшаем fontSize пока высота или ширина больше
        // в fitShape(true): true - значит не переносить части слов
        // Этот параметр убирает гарантию, что после fitShape width текста < hardWidth
        // Это нужно, чтобы одно слово писалось в одну строку и не переносилось

        if (this.symbolsCount == 0) {
            return;
        }

        const step = 1;
        for (var i = 0; i < 500; i++) {
            this.fitShape(true);
            if (this.height > this.hardHeight || this.width > this.hardWidth) {
                this.updateDims();
                break;
            }
            this.fontSize = getFontSize(this.fontSize + step);
            for (let k = 0; k < this.lines.length; k++) {
                this.lines[k].setTextSize(this.fontSize, 0, this.lines[k].items.length);
            }
            this.updateStylesValuesAndDims();
        }

        for (var i = 0; i < 500; i++) {
            this.fitShape(true);
            if (this.height < this.hardHeight && this.width < this.hardWidth) {
                this.updateDims();
                break;
            }
            this.fontSize -= step;
            // this.fontSize = getFontSize(this.fontSize - step);
            for (let k = 0; k < this.lines.length; k++) {
                this.lines[k].setTextSize(this.fontSize, 0, this.lines[k].items.length);
            }
            this.updateStylesValuesAndDims();
        }

        this.selection.loadSelectionSequential(selectionSequentialPosition);
        canv.redraw();
    }

    fitShape(moveOnlyWords = false) {
        this.updateDims(); // нужен, при вызове из shape, так как тут this.width == NaN, так как при shape.loadState->textPart.loadState у нас еще не было hardWidth и в textPart.updateDims мы посчитали textPart.width == NaN, textPart.startX == NaN
        this.fitShapeWidth(moveOnlyWords);
        this.fitShapeWidthReverse();
    }

    fitShapeWidth(moveOnlyWords) {
        var selectionSequentialPosition = this.selection.getSelectionSequential();

        var iterMax = this.lines.length;

        for (var i = 0; i < iterMax; i++) {
            var currentLine = this.lines[i];

            var overlay = currentLine.getOverlayToken(this.hardWidth);

            // Все влезает
            if (overlay["overlayPosition"] == -1) continue;

            // Единичные символы не переносим
            if (overlay["overlayPosition"] == 0) return;

            if (
                moveOnlyWords &&
                (overlay["tokenPosition"] == -1 || overlay["tokenPosition"] == 0)
            ) {
                continue;
            }

            var toTextLine = null;
            if (currentLine.enterExists || i == this.lines.length - 1) {
                toTextLine = new textLine(i + 1, this, false, currentLine.alignment);
                this.lines.splice(i + 1, 0, toTextLine);
                toTextLine.enterExists = currentLine.enterExists;
                iterMax++;
            } else {
                toTextLine = this.lines[i + 1];
            }

            currentLine.enterExists = false;

            var erasePosition = 0;

            // Первое слово целиком не переносим
            if (overlay["tokenPosition"] == -1 || overlay["tokenPosition"] == 0) {
                erasePosition = overlay["overlayPosition"];
            } else {
                erasePosition = overlay["tokenPosition"];
            }

            for (var k = currentLine.items.length - 1; k >= erasePosition; k--) {
                toTextLine.items.unshift(currentLine.items[k]);
            }

            currentLine.items.splice(
                erasePosition,
                currentLine.items.length - erasePosition,
            );
            this.updateDims();
        }

        this.selection.loadSelectionSequential(selectionSequentialPosition);
        this.updateDims();
    }

    fitShapeWidthReverse() {
        var selectionSequentialPosition = this.selection.getSelectionSequential();

        for (var i = 0; i < this.lines.length; i++) {
            if (i == this.lines.length - 1) continue;

            if (this.lines[i].enterExists) continue;

            var currentLine = this.lines[i];
            var nextLine = this.lines[i + 1];

            var freeSpace = this.hardWidth - currentLine.textWidth;

            var lastTokenEnd = nextLine.getAllowedTokens(freeSpace);

            if (lastTokenEnd != -1) {
                for (var k = 0; k <= lastTokenEnd; k++) {
                    currentLine.items.push(nextLine.items[k]);
                }

                nextLine.items.splice(0, lastTokenEnd + 1);

                if (nextLine.items.length == 0) {
                    currentLine.enterExists = nextLine.enterExists;
                    this.lines.splice(i + 1, 1);
                    i--;
                }

                this.updateDims(); // нужно обновить ширину у currentLine и nextLine
            }
        }

        this.selection.loadSelectionSequential(selectionSequentialPosition);
        this.updateDims();
    }

    // --------------------------------------------------------------------------------

    // Processors --------------------------------------------------------------------------------

    processSymbol() {
        const allSymbolsCount = countCharactersOfText(this.lines);

        if (allSymbolsCount >= MAX_TEXT_LENGTH) {
            showSnackbar(SNACKBAR_LIMIT_TEXT_1, {
                duration: SNACKBAR_TIME,
                type: "warning",
                id: "limit-text-1",
            });

            return;
        }

        this.aiGenerated = false;

        // тут у нас курсор
        var tmpItem = new textItem(
            canv.keyboard.command,
            this.fontSize,
            this.fontStyle,
            this.fontColor,
            this.fontBold,
            this.fontStrike,
            this.backgroundColor,
            this.symbolLink,
        );
        this.lines[this.selection.selectionStart["line"]].items.splice(
            this.selection.selectionStart["position"],
            0,
            tmpItem,
        );
        this.selection.moveCursorRight();
    }

    processEnter() {
        // тут у нас курсор
        var cursorLineIndex = this.selection.selectionStart["line"];
        var cursorLine = this.lines[cursorLineIndex];
        var cursorPosition = this.selection.selectionStart["position"];

        var toTextLine = null;
        if (cursorLine.enterExists || cursorLineIndex == this.lines.length - 1) {
            toTextLine = new textLine(
                cursorLineIndex + 1,
                this,
                cursorLine.bullettedList,
                cursorLine.alignment,
            );
            this.lines.splice(cursorLineIndex + 1, 0, toTextLine);
            toTextLine.enterExists = cursorLine.enterExists;
        } else {
            toTextLine = this.lines[cursorLineIndex + 1];
        }

        for (var i = cursorLine.items.length - 1; i >= cursorPosition; i--) {
            toTextLine.items.unshift(cursorLine.items[i]);
        }

        cursorLine.items.splice(
            cursorPosition,
            cursorLine.items.length - cursorPosition,
        );
        cursorLine.enterExists = true;

        this.selection.setCursor(cursorLineIndex + 1, 0);
    }

    processBackspace() {
        // заранее запоминаем курсор ли, потому что после removeSelection будет точно курсор
        var wasCursor = this.selection.isCursor();
        this.selection.removeSelection();

        this.aiGenerated = false;

        var cursorLineIndex = this.selection.selectionStart["line"];
        var cursorLine = this.lines[cursorLineIndex];
        var cursorPosition = this.selection.selectionStart["position"];

        if (!wasCursor) return;

        if (cursorPosition == 0 && cursorLineIndex != 0) {
            var prevLine = this.lines[cursorLineIndex - 1];

            this.selection.setCursor(cursorLineIndex - 1, prevLine.items.length);

            for (var k = 0; k < cursorLine.items.length; k++) {
                prevLine.items.push(cursorLine.items[k]);
            }
            this.lines.splice(cursorLineIndex, 1);

            if (!prevLine.enterExists) {
                // если enterExists небыло, то нужно еще раз сделать Backspace
                // Это актуально например для стикера, где могут быть строки без enterExists
                this.processBackspace();
            }

            prevLine.enterExists = cursorLine.enterExists;
        } else if (cursorPosition != 0) {
            cursorLine.items.splice(cursorPosition - 1, 1);
            this.selection.moveCursorLeft();
        }
    }

    processDelete() {
        // заранее запоминаем курсор ли, потому что после removeSelection будет точно курсор
        const wasCursor = this.selection.isCursor();
        this.selection.removeSelection();

        this.aiGenerated = false;

        const cursorLineIndex = this.selection.selectionStart["line"];
        const cursorLine = this.lines[cursorLineIndex];
        const cursorPosition = this.selection.selectionStart["position"];

        if (!wasCursor) return;

        const isLastInLine = cursorPosition === cursorLine.items.length;
        const isNotLastLine = Boolean(this.lines[cursorLineIndex + 1]);
        if (!isLastInLine) {
            // удалять следующий символ
            cursorLine.items.splice(cursorPosition, 1);
        } else if (isNotLastLine) {
            // перенос строки
            const nextLine = this.lines[cursorLineIndex + 1];
            nextLine.items.forEach((item) => {
                cursorLine.items.push(item);
            });
            this.lines.splice(cursorLineIndex + 1, 1);

            cursorLine.enterExists = nextLine.enterExists;
        }
    }

    processArrow() {
        if (canv.keyboard.isLeftArrow() || canv.keyboard.isUpArrow()) {
            var wasCursor = this.selection.isCursor();
            this.selection.selectionLeftSqueeze();
            if (wasCursor) {
                if (canv.keyboard.isLeftArrow()) {
                    this.selection.moveCursorLeft();
                } else {
                    this.selection.moveCursorUp();
                }
            }
        } else if (canv.keyboard.isRightArrow() || canv.keyboard.isDownArrow()) {
            var wasCursor = this.selection.isCursor();
            this.selection.selectionRightSqueeze();
            if (wasCursor) {
                if (canv.keyboard.isRightArrow()) {
                    this.selection.moveCursorRight();
                } else {
                    this.selection.moveCursorDown();
                }
            }
        }
    }

    processArrowShift() {
        if (canv.keyboard.isLeftArrow()) {
            this.selection.moveCursorLeft(true);
        } else if (canv.keyboard.isUpArrow()) {
            this.selection.moveCursorUp(true);
        } else if (canv.keyboard.isRightArrow()) {
            this.selection.moveCursorRight(true);
        } else {
            this.selection.moveCursorDown(true);
        }
    }

    processPasteCommon(lines) {
        if (!lines.length) return;

        let pasteLines = splitStringByMaxSymbols(lines, this.align);
        const useDefaultColor = this.parent === null;

        const pasteCharactersCount = countCharactersOfText(pasteLines);
        const countCharacters = characterCounter(this.selection, this.lines);

        if (MAX_TEXT_LENGTH < (countCharacters + pasteCharactersCount)) {
            showSnackbar(SNACKBAR_LIMIT_TEXT_2, {
                duration: SNACKBAR_TIME,
                type: "warning",
                id: "limit-text-2",
            });
            pasteLines = cutHtmlTextBySymbolsCount(
                pasteLines,
                MAX_TEXT_LENGTH - countCharacters,
            );
        }

        const generateLineTextItems = (line) => {
            const result = [];

            for (let i = 0; i < line.items.length; i++) {
                const res = line.items[i];
                const tmpItem = new textItem(
                    res.value,
                    this.fontSize,
                    this.fontStyle,
                    useDefaultColor ? this.fontColor : res.fontColor || this.fontColor,
                    res.fontBold || this.fontBold,
                    res.fontStrike || this.fontStrike,
                    res.backgroundColor || this.backgroundColor,
                );

                result.push(tmpItem);
            }

            return result;
        };

        const { startLine, startPosition, endLine, endPosition } =
            getActualSelection(this.selection);

        let cursorLine;
        let cursorPosition;

        if (startLine === endLine && startPosition === endPosition) {
            // кликнули в текст
            if (pasteLines.length === 1) {
                this.lines[startLine].items.splice(
                    startPosition,
                    0,
                    ...generateLineTextItems(pasteLines[0]),
                );
                cursorLine = startLine;
                cursorPosition = startPosition + pasteLines[0].items.length;
            } else {
                const lineStartItems = this.lines[startLine].items.slice(
                    0,
                    startPosition,
                );
                const lineEndItems = this.lines[startLine].items.slice(
                    startPosition,
                    this.lines[startLine].items.length,
                );

                for (let i = 0; i < pasteLines.length; i++) {
                    const lineItems = generateLineTextItems(pasteLines[i]);

                    if (i === 0) {
                        this.lines[startLine].items = [...lineStartItems, ...lineItems];
                    } else if (i === pasteLines.length - 1) {
                        const line = new textLine(
                            startLine + i,
                            this,
                            pasteLines[i].bullettedList,
                            pasteLines[i].align,
                        );

                        line.enterExists = pasteLines[i].enterExists;
                        line.items = [...lineItems, ...lineEndItems];
                        this.lines.splice(startLine + i, 0, line);
                    } else {
                        const line = new textLine(
                            startLine + i,
                            this,
                            pasteLines[i].bullettedList,
                            pasteLines[i].align,
                        );

                        line.enterExists = pasteLines[i].enterExists;
                        line.items = lineItems;
                        this.lines.splice(startLine + i, 0, line);
                    }
                }
                cursorLine = startLine + pasteLines.length - 1;
                cursorPosition = pasteLines[pasteLines.length - 1].items.length;
            }
        } else if (startLine === endLine) {
            // выделили текст в одной строчке
            if (pasteLines.length === 1) {
                this.lines[startLine].items.splice(
                    startPosition,
                    endPosition - startPosition,
                    ...generateLineTextItems(pasteLines[0]),
                );
                cursorLine = startLine;
                cursorPosition = startPosition + pasteLines[0].items.length;
            } else {
                const lineStartItems = this.lines[startLine].items.slice(
                    0,
                    startPosition,
                );
                const lineEndItems = this.lines[startLine].items.slice(
                    endPosition,
                    this.lines[startLine].items.length,
                );

                for (let i = 0; i < pasteLines.length; i++) {
                    const lineItems = generateLineTextItems(pasteLines[i]);

                    if (i === 0) {
                        this.lines[startLine].items = [...lineStartItems, ...lineItems];
                    } else if (i === pasteLines.length - 1) {
                        const line = new textLine(
                            endLine + i,
                            this,
                            pasteLines[i].bullettedList,
                            pasteLines[i].align,
                        );

                        line.enterExists = pasteLines[i].enterExists;
                        line.items = [...lineItems, ...lineEndItems];
                        this.lines.splice(startLine + i, 0, line);
                    } else {
                        const line = new textLine(
                            startLine + i,
                            this,
                            pasteLines[i].bullettedList,
                            pasteLines[i].align,
                        );

                        line.enterExists = pasteLines[i].enterExists;
                        line.items = lineItems;
                        this.lines.splice(startLine + i, 0, line);
                    }
                }
                cursorLine = startLine + pasteLines.length - 1;
                cursorPosition = pasteLines[pasteLines.length - 1].items.length;
            }
        } else {
            // выделили текст на разных строчках
            if (pasteLines.length === 1) {
                const endLineItems = [
                    ...this.lines[endLine].items.slice(
                        endPosition,
                        this.lines[endLine].items.length,
                    ),
                ];
                const lineItems = generateLineTextItems(pasteLines[0]);

                this.lines[startLine].items.splice(
                    startPosition,
                    this.lines[startLine].items.length,
                    ...[...lineItems, ...endLineItems],
                );
                this.lines.splice(startLine + 1, endLine - startLine);

                cursorLine = startLine;
                cursorPosition = startPosition + pasteLines[0].items.length;
            } else {
                const copiedLines = [...pasteLines];
                const startLineItems = generateLineTextItems(copiedLines[0]);
                const endLineItems = generateLineTextItems(
                    copiedLines[copiedLines.length - 1],
                );
                const middleLinesItems = copiedLines
                    .splice(1, copiedLines.length - 2)
                    .map(generateLineTextItems);

                this.lines[startLine].items.splice(
                    startPosition,
                    this.lines[startLine].items.length,
                    ...startLineItems,
                );
                this.lines[endLine].items.splice(0, endPosition, ...endLineItems);

                for (let i = 0; i < middleLinesItems.length; i++) {
                    const newLine = new textLine(
                        startLine + 1 + i,
                        this,
                        pasteLines[i + 1].bullettedList,
                        pasteLines[i + 1].align,
                    );
                    newLine.items = middleLinesItems[i];
                    newLine.enterExists = middleLinesItems[i].enterExists;

                    if (i === 0) {
                        this.lines.splice(startLine + 1, endLine - startLine - 1, newLine);
                    } else {
                        this.lines.splice(startLine + 1 + i, 0, newLine);
                    }
                }

                cursorLine = startLine + pasteLines.length - 1;
                cursorPosition = pasteLines[pasteLines.length - 1].items.length;
            }
        }

        this.selection.resetSelection({
            selectionStart: { line: cursorLine, position: cursorPosition },
            selectionEnd: { line: cursorLine, position: cursorPosition },
        });

        this.updateDims();

        return { cursorLine, cursorPosition };
    }

    handleSimpleTextToHtml(text) {
        const textLines = text.split("\n");
        const lines = [];

        for (let i = 0; i < textLines.length; i++) {
            const line = {
                items: [],
                bullettedList: this.bullettedList,
                align: this.align,
            };

            for (let k = 0; k < textLines[i].length; k++) {
                line.items.push({
                    value: textLines[i][k],
                    fontColor: this.fontColor,
                    fontBold: this.fontBold,
                    backgroundColor: this.backgroundColor,
                    fontStrike: this.fontStrike,
                });
            }

            lines.push(line);
        }

        return lines;
    }

    async processPaste() {
        const textItemProcessor = function (messageStr) {
            var ifJson = false;
            var jsonOb = null;
            try {
                jsonOb = JSON.parse(messageStr);
                ifJson = true;
            } catch (e) { }

            if (ifJson && jsonOb != null && jsonOb["isJespoDumps"] != undefined) {
                // не вставляем в текст isJespoDumps код виджетов. Если кто-то скопировал виджет, а потом решил вставить это в текст
                return;
            }

            const lines = this.handleSimpleTextToHtml(messageStr);
            return this.processPasteCommon(lines);
        };

        const result = await processDataFromClipboard({
            textItemProcessor: textItemProcessor.bind(this),
            htmlItemProcessor: this.processPasteCommon.bind(this),
        });

        if (result?.lastProcessorResult?.cursorLine !== undefined) {
            const { cursorLine, cursorPosition } = result.lastProcessorResult;
            this.selection.setCursor(cursorLine, cursorPosition);
            this.aiGenerated = false;
        }

        eventEmitter.dispatch('paste.done');
    }

    processCopy() {
        var selectedText = "";

        var sortedSelection = this.selection.getSortedSelection();

        for (
            var i = sortedSelection["selectionFirst"]["line"];
            i <= sortedSelection["selectionLast"]["line"];
            i++
        ) {
            var currentLine = this.lines[i];

            var startIndex = 0;
            var endIndex = currentLine.items.length;

            if (i == sortedSelection["selectionFirst"]["line"]) {
                startIndex = sortedSelection["selectionFirst"]["position"];
            }

            if (i == sortedSelection["selectionLast"]["line"]) {
                endIndex = sortedSelection["selectionLast"]["position"];
            }

            selectedText += this.lines[i].getIntervalText(startIndex, endIndex);

            if (
                i < sortedSelection["selectionLast"]["line"] &&
                currentLine.enterExists
            ) {
                selectedText += " ";
            }
        }

        saveTextToClipboard(selectedText);
    }

    // --------------------------------------------------------------------------------

    // Используется при loadState full, shape resize, sticker resize ...
    // Надо убрать это в updateDims, чтобы selection там обновлялся, проверялся

    setCursorToEnd() {
        this.selection.setCursor(
            this.lines.length - 1,
            this.lines[this.lines.length - 1].items.length,
        );
    }

    // Используется при fastAction

    clearText() {
        this.lines = [];
        this.lines.push(new textLine(0, this, this.bullettedList, this.align));
        this.updateDims();
    }

    // Использутcя в canvasController pasteText, при создании Frame
    setText(textData) {
        this.clearText();

        let text = textData;

        if (textData.length > MAX_TEXT_LENGTH) {
            showSnackbar(SNACKBAR_LIMIT_TEXT_1, {
                duration: SNACKBAR_TIME,
                type: "warning",
                id: "limit-text-1",
            });

            text = textData.slice(0, MAX_TEXT_LENGTH);
        }

        const textLines = splitStringByMaxSymbols(convertTextToLines(text));

        for (let index = 0; index < textLines.length; index++) {
            const line = textLines[index];

            if (index === 0) {
                for (let i = 0; i < line.items.length; i++) {
                    var newItem = new textItem(
                        line.items[i].value,
                        this.fontSize,
                        this.fontStyle,
                        this.fontColor,
                        this.fontBold,
                        this.fontStrike,
                        this.backgroundColor,
                    ); // тут не используем this.symbolLink
                    this.lines[0].items.push(newItem);
                }

                if (textLines.length > 1) {
                    this.lines[0].enterExists = true;
                }
            } else if (index === textLines.length - 1 && line.items.length < 1) {
                continue;
            } else {
                const newLine = new textLine(
                    this.lines.length,
                    this,
                    this.bullettedList,
                    this.align,
                );
                newLine.enterExists = true;

                for (let i = 0; i < line.items.length; i++) {
                    const newItem = new textItem(
                        line.items[i].value,
                        this.fontSize,
                        this.fontStyle,
                        this.fontColor,
                        this.fontBold,
                        this.fontStrike,
                        this.backgroundColor,
                    );
                    newLine.items.push(newItem);
                }

                this.lines.splice(index, 0, newLine);
            }
        }

        this.updateDims();
    }

    setHtmlText(lines) {
        this.clearText();

        let charactersCount = MAX_TEXT_LENGTH;

        const textLines = splitStringByMaxSymbols(lines);

        for (let i = 0; i < textLines.length; i++) {
            let symbols = textLines[i].items;

            if (charactersCount < symbols.length) {
                symbols = textLines[i].items.slice(0, charactersCount);
            }

            charactersCount -= symbols.length;

            const textItems = [];

            for (let k = 0; k < symbols.length; k++) {
                const item = symbols[k];

                textItems.push(
                    new textItem(
                        item.value,
                        this.fontSize,
                        this.fontStyle,
                        item.fontColor || this.fontColor,
                        item.fontBold,
                        item.fontStrike,
                        item.backgroundColor,
                    ),
                );
            }

            if (i === 0) {
                this.lines[0].items = textItems;
                this.lines[0].enterExists = true;
                this.lines[0].bullettedList = textLines[0].bullettedList;
                this.lines[0].align = textLines[0].align;
            } else {
                const newLine = new textLine(
                    i,
                    this,
                    textLines[i].bullettedList,
                    textLines[i].align,
                );

                newLine.enterExists = true;
                newLine.items = textItems;

                this.lines.splice(i, 0, newLine);
            }

            if (charactersCount <= 0) {
                showSnackbar(SNACKBAR_LIMIT_TEXT_1, {
                    duration: SNACKBAR_TIME,
                    type: "warning",
                    id: "limit-text-1",
                });

                break;
            }
        }

        this.updateDims();
    }

    getTextByLines() {
        var text = ``;

        for (var i = 0; i < this.lines.length; i++) {
            text += this.lines[i].getText();
            if (i < this.lines.length - 1 && this.lines[i].enterExists) text += "\n";
        }

        return text;
    }

    // для frame side menu
    getText() {
        var text = ``;

        for (var i = 0; i < this.lines.length; i++) {
            text += this.lines[i].getText();
            if (i < this.lines.length - 1 && this.lines[i].enterExists) text += " ";
        }

        return text;
    }

    // используется в searchWindow для поиска
    searchText(text) {
        const concatText = this.getText().toLowerCase();

        if (concatText.includes(text)) {
            return [
                {
                    status: true,
                    textWidget: this,
                    mode: "text",
                },
            ];
        }

        return [];
    }

    // выставляет isFocused, включает курсор и обновляет его позицию
    focusText() {
        this.isFocused = true; // isFocused внутри textWidget значит, что идет редактирование. Это влияет на отрисовку selection и на обработчики widget menu
        this.setCursorToEnd();
        this.selection.reScheduleCursor();
        this.selection.startSelection(); // ставим курсор в нужное место. Повотряем что написано в textWidget в TryAction("mousedown" | "mouseup")
        this.selection.stopSelection();
    }

    focusTextByEnter() {
        this.isFocused = true; // isFocused внутри textWidget значит, что идет редактирование. Это влияет на отрисовку selection и на обработчики widget menu
        this.setCursorToEnd();
        this.selection.reScheduleCursor();
    }

    unfocusText() {
        this.isFocused = false;
        this.selection.offCursor();

        this.isLinkEdit = false;
        canv.appInterface.linkWidgetMenuWindow.hide();
        canv.appInterface.editTextWindow.hide();
    }

    // Base ---------------------------------------------------------

    translate(x, y) {
        this.startX += x;
        this.startY += y;
        this.updateDims();
    }

    resize(x, y, catchPointer) {
        if (!this.isResizing) return;
        if (this.symbolsCount == 0) return;
        if (x == 0 && y == 0) return;

        if (!this.resizeStartCatchPointer) {
            this.resizeStartCatchPointer = catchPointer;
        }

        if (["sideRight", "sideLeft"].includes(catchPointer)) {
            // side resize
            if (!this.shapeMode) {
                this.shapeMode = true;
                this.hardWidth = this.width;
            }
            this.hardWidth += x;
            this.Fit();
            this.updateDims();
        } else if (
            ["upRight", "upLeft", "bottomRight", "bottomLeft"].includes(catchPointer)
        ) {
            // corner resize
            const aspectRatio = this.width / this.height;
            const absX = Math.abs(x);
            const absY = Math.abs(y);

            let newWidth, newHeight;
            if (absX > absY) {
                // если движение было больше по X - берем за основу его
                newWidth = this.width + x;
                // потом считаем высоту учитывая соотношение сторон
                newHeight = newWidth / aspectRatio;
            } else {
                // если движение было больше по Y - берем за основу его
                newHeight = this.height + y;
                // потом считаем ширину учитывая соотношение сторон
                newWidth = newHeight * aspectRatio;
            }

            // считаем как поменялась высота/ширина
            const widthScale = newWidth / this.width;
            const heightScale = newHeight / this.height;
            const scale = Math.min(widthScale, heightScale);
            // считаем размер шрифта с учетом этого же масштаба
            const newFontSize = getFontSize(this.fontSize * scale);
            // если размер шрифта по итогу не поменялся или произошел "разворот" фигуры - ничего не делаем
            if (
                newFontSize === this.fontSize ||
                this.resizeStartCatchPointer !== catchPointer
            ) {
                return;
            }
            this.fontSize = newFontSize;
            this.lines.forEach((line) => {
                line.setTextSize(this.fontSize);
            });

            this.hardWidth = newWidth;
            this.width = newWidth;
            this.height = newHeight;
        }
    }

    resizingStart = () => {
        this.isResizing = true;
    };

    resizingStop = (catchPointer) => {
        this.isResizing = false;
        this.resizeStartCatchPointer = null;
    };

    beforeDeletion() {
        // это можно было бы не писать. Нужно чтобы сразу убирать всплывашку при удалении виджета.
        if (this.isOpenedMyLink()) {
            canv.appInterface.textWidgetLinkWindow.hide();
        }
    }

    // ----------------------------------------------------------------

    // links -------------------------------------------------------------

    linkInterfaceShow() {
        const allowedMode =
            canv.toolBox.TryGetToolByName("cursorTool").isMode("") ||
            canv.toolBox.TryGetToolByName("cursorTool").isMode("interaction");

        if (!allowedMode) {
            if (this.isOpenedMyLink()) {
                canv.appInterface.textWidgetLinkWindow.hide();
            }
            return;
        }

        if (this.isOpenedMyLink()) {
            this.linkHighlight(
                canv.appInterface.textWidgetLinkWindow.startData,
                canv.appInterface.textWidgetLinkWindow.endData,
            );
        }

        const linkWordData = this.linkWordGet();
        if (linkWordData.linkSymbolData == null) {
            return;
        }

        if (canv.appInterface.textWidgetLinkWindow.isOpened) {
            if (
                canv.appInterface.textWidgetLinkWindow.isEqual(
                    linkWordData.startData,
                    linkWordData.endData,
                    linkWordData.linkSymbolData,
                    this,
                )
            ) {
                canv.appInterface.textWidgetLinkWindow.rerequestRepeater();
            } else if (!canv.appInterface.textWidgetLinkWindow.isMouseOverMenu()) {
                canv.appInterface.textWidgetLinkWindow.hide();
            }
        } else {
            canv.appInterface.textWidgetLinkWindow.show(
                linkWordData.linkSymbolData,
                linkWordData.startData,
                linkWordData.endData,
                this,
            );
            this.linkHighlight(linkWordData.startData, linkWordData.endData);
        }
    }

    /**
     * Извлекает данные о связанном слове (ссылке) на основе текущего положения символа.
     *
     * - Сначала получает символ-ссылку с помощью `linkWordGetSymbol`.
     * - Если символ отсутствует (`null`), возвращает объект с `linkSymbolData: null`.
     * - Затем определяет предикат для поиска слов, связанных с этой ссылкой.
     * - Использует метод `selection.findWord` для нахождения диапазона слова.
     * - Возвращает объект с данными о найденной ссылке и её позициях.
     *
     * type LinkSymbolData = {
     *     symbol: {
     *         link: string;
     *         [key: string]: any; // если есть другие поля
     *     };
     *     line: number;
     *     position: number;
     *     [key: string]: any; // на случай других полей
     * };
     * type WordPosition = {
     *     line: number;
     *     column: number;
     *     [key: string]: any;
     * };
     *
     * type LinkWordResult =
     *     | {
     *           linkSymbolData: LinkSymbolData;
     *           startData: WordPosition;
     *           endData: WordPosition;
     *       }
     *     | {
     *           linkSymbolData: null;
     *       };
     */
    linkWordGet() {
        const linkSymbolData = this.linkWordGetSymbol();
        if (linkSymbolData.symbol == null || !linkSymbolData.symbol?.link) {
            return {
                linkSymbolData: null,
            };
        }

        const predicat = (item) => item.link == linkSymbolData.symbol.link;

        const result = this.selection.findWord(
            linkSymbolData.line,
            linkSymbolData.position,
            predicat,
            false,
        );

        const linkWordData = {
            linkSymbolData: linkSymbolData,
            startData: result.start,
            endData: result.end,
        };

        return linkWordData;
    }

    /**
     *
     * type SymbolItem = {
     *     link: string;
     *     startX: number;
     *     getWidth: () => number;
     *     [key: string]: any; // если есть дополнительные поля
     * };
     *
     * type LinkSymbolFound = {
     *     symbol: SymbolItem;
     *     line: number;
     *     position: number;
     * };
     *
     * type LinkSymbolNotFound = {
     *     symbol: null;
     * };
     *
     * type LinkSymbolData = LinkSymbolFound | LinkSymbolNotFound;
     * */
    linkWordGetSymbol() {
        var pt = canv.actionManager.getActionPoint();

        for (let k = 0; k < this.lines.length; k++) {
            const line = this.lines[k];

            if (!line.isOverLineBox()) continue; // для проверки Y

            for (let i = 0; i < line.items.length; i++) {
                if (!line.items[i].link) continue;
                if (
                    pt.x >= line.items[i].startX &&
                    pt.x <= line.items[i].startX + line.items[i].getWidth()
                ) {
                    return {
                        symbol: line.items[i],
                        line: k,
                        position: i,
                    };
                }
            }
        }

        return {
            symbol: null,
        };
    }

    linkHighlight(wordSelectDataStart, wordSelectDataEnd) {
        for (
            var i = wordSelectDataStart["line"];
            i <= wordSelectDataEnd["line"];
            i++
        ) {
            var selectionLine = this.lines[i];

            var selectionStartX = 0;
            var startIndex = 0;
            var endIndex = selectionLine.items.length;

            if (i == wordSelectDataStart["line"]) {
                startIndex = wordSelectDataStart["position"];
            }

            if (i == wordSelectDataEnd["line"]) {
                endIndex = wordSelectDataEnd["position"];
            }

            if (startIndex == 0) {
                selectionStartX = selectionLine.textStartX;
            } else {
                selectionStartX =
                    selectionLine.items[startIndex - 1].startX +
                    selectionLine.items[startIndex - 1].getWidth();
            }

            var selectionWidth = selectionLine.getIntervalWidth(
                startIndex,
                endIndex - 1,
            );

            canv.ctx.save();
            canv.ctx.fillStyle = "#0B99FF";
            canv.ctx.globalAlpha = 0.2;
            canv.ctx.lineWidth = 2 / canv.m[0];
            canv.ctx.fillRect(
                selectionStartX,
                selectionLine.startY,
                selectionWidth,
                -selectionLine.textHeight,
            );
            canv.ctx.restore();
        }

        canv.redraw();
    }

    isOpenedMyLink() {
        if (
            canv.appInterface.textWidgetLinkWindow.isOpened &&
            canv.appInterface.textWidgetLinkWindow.textWidget == this
        ) {
            return true;
        }

        return false;
    }

    // --------------------------------------------------------------------

    // Menu -------------------------------------------------------

    getAffectedLines(forceAll = false) {
        const result = [];

        var sortedSelection = this.selection.getSortedSelection();

        var startLine = 0;
        var endLine = this.lines.length - 1;

        var selectionOnly = this.isFocused && !forceAll;
        if (selectionOnly) {
            startLine = sortedSelection["selectionFirst"]["line"];
            endLine = sortedSelection["selectionLast"]["line"];
        }

        for (var i = startLine; i <= endLine; i++) {
            var currentLine = this.lines[i];

            var startIndex = 0;
            var endIndex = currentLine.items.length;

            if (selectionOnly && i == sortedSelection["selectionFirst"]["line"]) {
                startIndex = sortedSelection["selectionFirst"]["position"];
            }

            if (selectionOnly && i == sortedSelection["selectionLast"]["line"]) {
                endIndex = sortedSelection["selectionLast"]["position"];
            }

            result.push({
                index: i,
                startIndex: startIndex,
                endIndex: endIndex,
            });
        }

        return result;
    }

    setAffectedItemsUniqValues(
        widgetOptions,
        field,
        wmName,
        widgetUID,
    ) {
        const affectedLines = this.getAffectedLines();
        const values = [];

        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            this.lines[lineInfo["index"]].enrichItemValues(
                field,
                lineInfo["startIndex"],
                lineInfo["endIndex"],
                values,
            );
        }

        if (values.length == 0) {
            if (field == "color") {
                values.push(this.fontColor);
            } else if (field == "bold") {
                values.push(this.fontBold);
            } else if (field == "strike") {
                values.push(this.fontStrike);
            } else if (field == "size") {
                values.push(this.fontSize);
            } else if (field == "link") {
                values.push(this.symbolLink);
            }
        }

        let wmUID = this.UID;
        if (widgetUID != undefined) {
            wmUID = widgetUID;
        }

        for (var i = 0; i < values.length; i++) {
            widgetOptions.push(new widgetOption(wmName, values[i], wmUID));
        }
    }

    setAffectedLinesValues(widgetOptions, field, wmName) {
        const affectedLines = this.getAffectedLines();

        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            if (field == "bulletedList") {
                widgetOptions.push(
                    new widgetOption(
                        wmName,
                        this.lines[lineInfo["index"]].bullettedList,
                        this.UID,
                    ),
                );
            } else if (field == "align") {
                widgetOptions.push(
                    new widgetOption(
                        wmName,
                        this.lines[lineInfo["index"]].alignment,
                        this.UID,
                    ),
                );
            }
        }
    }

    setEditTextMobile(widgetOptions, UID) {
        if (isMobile()) {
            widgetOptions.push(new widgetOption("mobileEdit", "", UID));
        }
    }

    getWidgetOptions() {
        var widgetOptions = [];

        this.setAffectedItemsUniqValues(widgetOptions, "color", "textColor");
        this.setAffectedItemsUniqValues(widgetOptions, "bold", "textBold");
        this.setAffectedItemsUniqValues(widgetOptions, "strike", "textStrike");
        this.setAffectedItemsUniqValues(widgetOptions, "link", "link");

        this.setEditTextMobile(widgetOptions, this.UID);

        this.setAffectedLinesValues(widgetOptions, "bulletedList", "textBulleted");
        this.setAffectedLinesValues(widgetOptions, "align", "textAlign");

        this.setAffectedItemsUniqValues(widgetOptions, "size", "textSize");

        return widgetOptions;
    }

    getHandlerForOption(name) {
        if (name == "textColor") {
            return this.menuTextColor;
        } else if (name == "textBackground") {
            return this.menuTextBackground;
        } else if (name == "textBold") {
            return this.menuTextBold;
        } else if (name == "textStrike") {
            return this.menuTextStrike;
        } else if (name == "textBulleted") {
            return this.menuTextBulleted;
        } else if (name == "textAlign") {
            return this.menuTextAlign;
        } else if (name == "textSize") {
            return this.menuTextSize;
        } else if (name == "link") {
            return this.menuLink;
        } else if (name === "mobileEdit") {
            return () => this.menuMobileEdit(this);
        }
    }

    menuMobileEdit(needSelect = true) {
        if (!isMobile()) {
            return;
        }

        if (needSelect !== false) {
            canv.toolBox
                .TryGetToolByName("cursorTool")
                .setSelectedAndInteraction(this.parent);
        }

        canv.appInterface.editTextWindow.show(this);
    }

    menuMobileEditSet(textData = "") {
        this.setText(textData);
        this.selection.stopSelection();
        this.isFocused = false;
        this.updateDims();
        this.Fit();
    }

    /**
     * @param {string} currentLink
     * @param {(() => void) | null} [extraUpdate=null]
     * @returns {void}
     */
    menuLink(currentLink, extraUpdate = null) {
        if (!this.isFocused) {
            // сюда должны попадать только текстовые виджеты
            if (this.parent != null) {
                // текстовый виджет
                canv.toolBox
                    .TryGetToolByName("cursorTool")
                    .setSelectedAndInteraction(this.parent);
                this.selection.selectAll();
            } else {
                // нужно быть isFocusd, иначе нету selection
                return;
            }
        } else if (this.selection.isCursor()) {
            const sortedSelection = this.selection.getSortedSelection();

            if (
                sortedSelection["selectionFirst"]["position"] > 0 &&
                this.lines[sortedSelection["selectionFirst"]["line"]].items[
                    sortedSelection["selectionFirst"]["position"] - 1
                ].link != ""
            ) {
                const predicat = function (item) {
                    return (
                        item.link ==
                        this.lines[sortedSelection["selectionFirst"]["line"]].items[
                            sortedSelection["selectionFirst"]["position"] - 1
                        ].link
                    );
                };
                const result = this.selection.findWord(
                    sortedSelection["selectionFirst"]["line"],
                    sortedSelection["selectionFirst"]["position"] - 1,
                    predicat.bind(this),
                ); // тут нужен bind у predicat, так как в нем используется this
                this.selection.setSelection(
                    result["start"]["line"],
                    result["start"]["position"],
                    result["end"]["line"],
                    result["end"]["position"],
                );
            }
        }

        this.isLinkEdit = true;
        canv.widgetMenu.hide();
        canv.appInterface.linkWidgetMenuWindow.show(currentLink, this, extraUpdate);
    }

    menuLinkSet(link) {
        // тут мы всегда isFocused, так как linkWidgetMenuWindow это часть интерфейса при interaction.
        if (this.selection.isCursor()) {
            var cursorLine = this.lines[this.selection.selectionStart["line"]];
            for (var i = 0; i < link.length; i++) {
                var cursorPosition = this.selection.selectionStart["position"];
                var tmpItem = new textItem(
                    link[i],
                    this.fontSize,
                    this.fontStyle,
                    this.fontColor,
                    this.fontBold,
                    this.fontStrike,
                    this.backgroundColor,
                    link,
                );
                cursorLine.items.splice(cursorPosition, 0, tmpItem);
                this.selection.moveCursorRight();
            }
            this.updateDims();
            this.Fit();
        } else {
            const affectedLines = this.getAffectedLines();

            for (let i = 0; i < affectedLines.length; i++) {
                const lineInfo = affectedLines[i];
                this.lines[lineInfo["index"]].setLink(
                    link,
                    lineInfo["startIndex"],
                    lineInfo["endIndex"],
                );
            }
        }

        // не делаем this.symbolLink = link, так как this.symbolLink будем задавать через updateStyle. Для остальных настроек аналогичная строка нужна, если, например, поменять цвет при курсоре. Тут же при курсоре мы добавим текст (вставив ссылку) и обновим настройку в updateStyles
        this.isLinkEdit = false;
        canv.widgetMenu.reopen();
        canv.redraw();
        if (this.isOpenedMyLink()) {
            canv.appInterface.textWidgetLinkWindow.hide();
        }
    }

    menuTextSize(size, forceAll = false) {
        const affectedLines = this.getAffectedLines(true); // всегда выставляем размер всем буквам

        this.fontSize = getFontSize(size);
        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            this.lines[lineInfo["index"]].setTextSize(
                this.fontSize,
                lineInfo["startIndex"],
                lineInfo["endIndex"],
            );
        }

        this.updateStylesValuesAndDims();
        this.Fit();
    }

    menuTextColor(value, forceAll = false, setDefault = true) {
        this.fontColor = value;

        const affectedLines = this.getAffectedLines(forceAll);

        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            this.lines[lineInfo["index"]].setTextColor(
                value,
                lineInfo["startIndex"],
                lineInfo["endIndex"],
            );
        }

        if (setDefault) {
            var textTool = canv.toolBox.TryGetToolByName("textTool");
            // Сейчас везде вызывается menuTextColor без setDefault==false и выставляют цвет текста на белый (фреймы, фигуры, ...), поэтому пока убираем
            //textTool.setColor(this.fontColor);
        }
    }

    menuTextBold(mode, forceAll = false) {
        this.fontBold = mode;

        const affectedLines = this.getAffectedLines(forceAll);

        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            this.lines[lineInfo["index"]].setTextBold(
                mode,
                lineInfo["startIndex"],
                lineInfo["endIndex"],
            );
        }

        this.updateDims();
        this.Fit();
    }

    menuTextStrike(isStriked) {
        this.fontStrike = isStriked;

        const affectedLines = this.getAffectedLines();

        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            this.lines[lineInfo["index"]].setTextStrike(
                isStriked,
                lineInfo["startIndex"],
                lineInfo["endIndex"],
            );
        }
    }

    menuTextBulleted(isBulleted, forceAll = false) {
        this.bullettedList = isBulleted;

        const affectedLines = this.getAffectedLines(forceAll);

        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            this.lines[lineInfo["index"]].setTextBulleted(isBulleted);
        }

        this.updateDims();
        this.Fit();
    }

    menuTextAlign(align, forceAll = false) {
        this.align = align;

        const affectedLines = this.getAffectedLines(forceAll);

        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            this.lines[lineInfo["index"]].setTextAlign(align);
        }

        this.updateDims();
    }

    menuTextBackground(color) {
        this.backgroundColor = color;

        const affectedLines = this.getAffectedLines();

        for (let i = 0; i < affectedLines.length; i++) {
            const lineInfo = affectedLines[i];
            this.lines[lineInfo["index"]].setBackgroundColor(
                color,
                lineInfo["startIndex"],
                lineInfo["endIndex"],
            );
        }
    }

    selectFontStyle() {
        // тут немного некорректно. Получается при выpjdt loadState при interaction мы поставим cursor setCursorToEnd, а стил возьмем у первой буквы.
        for (let i = 0; i < this.lines.length; i++) {
            if (this.lines[i].items.length != 0) {
                this.selection.updateStylesSymbol(this.lines[i].items[0]);
                break;
            }
        }
    }

    // ---------------------------------------------------------------

    // Для statesManager

    getFixType(reason) {
        var fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;
        }

        if (this.needFullState) {
            // this.needFullState = false; делать нельзя, так как getState дергается два раза для одного эвента
            // для этого instance текста мы будем сохранять всегда full
            return "full";
        }

        return fixType;
    }

    getState(fixType) {
        var state = {};

        switch (fixType) {
            case "move":
                state["startX"] = this.startX;
                state["startY"] = this.startY;
                break;
            case "full":
                state["UID"] = this.UID;
                state["parentUID"] = this.parentUID;
                state["entityType"] = "textWidget";
                state["startX"] = this.startX;
                state["startY"] = this.startY;

                state["shapeMode"] = this.shapeMode;
                state["hardWidth"] = this.hardWidth;

                state['aiGenerated'] = this.aiGenerated;

                state["lines"] = [];

                for (var i = 0; i < this.lines.length; i++) {
                    state["lines"].push(this.lines[i].getState("full"));
                }

                // зануляем всегда, сработает в loadState при загрузке доски первым пользователем. Далее уже не будем смотреть на эти поля
                state["miroX"] = 0;
                state["miroY"] = 0;
                state["miroWidth"] = 0;

                break;
        }

        return state;
    }

    loadState(stateObj, fixType) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);

                if ('aiGenerated' in stateObj) {
                    this.aiGenerated = getBoolProperty('aiGenerated', stateObj);
                }

                // Не выгружать! и не загружать! сбрасывать в loadState full для this.parent != 0????
                // на бэке не сохраняем
                if (stateObj["shapeMode"] != undefined)
                    this.shapeMode = getBoolProperty("shapeMode", stateObj);
                if (stateObj["hardWidth"] != undefined)
                    this.hardWidth = getNumberProperty("hardWidth", stateObj);

                for (var i = 0; i < stateObj["lines"].length; i++) {
                    if (this.lines.length == i) {
                        // create new line
                        this.lines.push(
                            new textLine(i, this, this.bullettedList, this.align),
                        );
                    }
                    this.lines[i].loadState(stateObj["lines"][i], "full");
                }

                if (this.lines.length > stateObj["lines"].length) {
                    this.lines.splice(
                        stateObj["lines"].length,
                        this.lines.length - stateObj["lines"].length,
                    );
                }

                if (this.lines.length == 0) {
                    // Бывает при загрузке из импорта Miro
                    this.lines.push(
                        new textLine(0, this, this.bullettedList, this.align),
                    );
                }

                if (this.isFocused) {
                    this.setCursorToEnd();
                }

                if (this.isOpenedMyLink()) {
                    canv.appInterface.textWidgetLinkWindow.hide();
                }
        }

        this.selectFontStyle();
        this.updateStylesValuesAndDims();

        if (
            stateObj["miroX"] != undefined &&
            stateObj["miroY"] != undefined &&
            stateObj["miroWidth"] != undefined &&
            stateObj["miroWidth"] != 0
        ) {
            // canv.eventManager.openRecord();
            // this.fixState("full");
            this.shapeMode = true;
            this.hardWidth = stateObj["miroWidth"];
            this.Fit(); // обновляем учитывая hardWidth
            this.shapeMode = false;
            this.hardWidth = 0;

            this.startX = stateObj["miroX"] - this.textWidthForMiro / 2;
            this.startY =
                stateObj["miroY"] - this.height / 2 + this.lines[0].textHeight;
            this.updateDims();
            this.needFullState = true;
            // this.saveState("full");
            // canv.eventManager.closeRecord();
        }
    }

    highlightText(text) {
        let startIndex = this.getText().toLowerCase().indexOf(text);
        let endIndex = startIndex + text.length - 1;

        for (var i = 0; i < this.lines.length; i++) {
            if (startIndex === -1) break;

            this.lines[i].highlightText(startIndex, endIndex);

            const k = this.lines[i].enterExists ? 1 : 0;
            startIndex = startIndex - (this.lines[i].items.length + k);
            endIndex = endIndex - (this.lines[i].items.length + k);
        }
    }

    constructorName = "text";
    startX = 0;
    startY = 0;
    height = 0;
    width = 0;
    lines = [];
    symbolsCount = 0;

    shapeMode = false;
    stickerMode = false;
    hardWidth = 0;
    hardHeight = 0;
    onlyLineMode = false; // можно не выгружать, проставляется в виджетах
    isFocused = false;
    isLinkEdit = false;
    isEditTextMobile = false;
    isResizing = false;
    /**
     * @type {string | null}
     */
    resizeStartCatchPointer = null;

    emptyLineHeight = 0;
    onlyEmptyLineWidth = 0;

    textWidthForMiro = 0; // используется только в момент загрузки доски из Миро для того, чтобы взять ширину именно самого текста

    fontSize = 30;
    fontStyle = "sans-serif";
    fontColor = "#2d2d2d";
    fontBold = "";
    fontStrike = false;
    backgroundColor = "transparent";
    bullettedList = false;
    align = "left";
    symbolLink = "";

    keepMinWidth = true;
    greyText = "";
    greyTextWidth = 0;
    needFullState = false;
    /**
     * @type {boolean}
    */
    aiGenerated = false;
}
